import { Center, Loader } from '@mantine/core'
import { useMutation } from '@tanstack/react-query'
import { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { accountApi } from '@/api/accountApi'
import { getApiError } from '@/api/helpers/apiError'
import { AuthLayout } from '@/components/layout/AuthLayout/AuthLayout'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { RouterPath } from '@/router/paths'
import { useSession } from '@/session/SessionContext'
import { ErrorWithBackToLogin } from './ErrorWithBackToLogin'
import { parseState } from './ssoUtils'

const OAUTH_PROVIDERS = ['google', 'microsoft']

export const OauthRedirect = () => {
  const navigate = useNavigate()

  const { provider } = useParams()
  const [searchParams] = useSearchParams()

  const { refreshSession } = useSession()

  const code = searchParams.get('code')
  const state = parseState(searchParams.get('state'))
  const redirectTo = state?.redirectTo ?? RouterPath.root
  const token = state?.token

  const {
    mutate: oauthLogin,
    isPending,
    isError,
    isSuccess,
    error
  } = useMutation({
    mutationFn: accountApi.ssoLogin,
    onSuccess: async () => {
      await refreshSession()
      void navigate(redirectTo)
    }
  })

  useEffect(() => {
    if (code && provider && OAUTH_PROVIDERS.includes(provider)) {
      oauthLogin({
        provider: provider as 'google' | 'microsoft',
        payload: {
          code,
          token // present only for signup
        }
      })
    }
  }, [code, token, provider, oauthLogin])

  if (!code || !provider || !OAUTH_PROVIDERS.includes(provider)) {
    return (
      <AuthLayout variant="large">
        <ErrorWithBackToLogin
          errorMessage={
            <FormattedMessage id="auth.sso.errors.invalidRedirect" />
          }
        />
      </AuthLayout>
    )
  }

  if (isError) {
    return (
      <AuthLayout variant="large">
        <ErrorWithBackToLogin
          errorMessage={
            getApiError(error).status === 404 ? (
              <FormattedMessage id="auth.sso.errors.accountNotFound" />
            ) : (
              <FormattedMessage id="auth.sso.errors.other" />
            )
          }
        />
      </AuthLayout>
    )
  }

  if (isPending) {
    return (
      <Center h={200}>
        <Loader />
      </Center>
    )
  }

  if (isSuccess) {
    return (
      <AuthLayout variant="large">
        <PlatformAlert variant="success">
          <FormattedMessage id="auth.sso.success" />
        </PlatformAlert>
      </AuthLayout>
    )
  }

  return null
}
